import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Grid } from '@mui/material';

import { AvailableResources, UnitApi } from '@/api';
import { RenderFormField } from '@/components';
import { LocalStorageItems } from '@/constants';
import {
  IEquipment,
  ILineLayer,
  IPointLayer,
  IPolygonLayer,
  IResource,
  IUnitType,
  IVehicle,
  ResourceType,
} from '@/models';
import { useToastStore } from '@/store';

import { UnitResourceFormFields } from '../data';

interface UnitResourceFormProps {
  cfsId?: string;
  unitId?: string;
  agencyId?: string;
  withUnitOnly?: boolean;
  currentUnitType?: IUnitType;
  resources?: AvailableResources;
  isForReassignment?: boolean;
  skipValidation?: boolean;
  setOutsideResources?: (resources: AvailableResources) => void;
}

enum ResourceItemType {
  USERS = 'users',
  VEHICLES = 'vehicles',
  EQUIPMENTS = 'equipments',
  POLYGONS = 'polygons',
  POINTS = 'points',
  LINES = 'lines',
}

export const UnitResourceForm: FC<UnitResourceFormProps> = ({
  unitId,
  agencyId,
  currentUnitType,
  withUnitOnly,
  resources: passedResources,
  isForReassignment,
  skipValidation,
  setOutsideResources,
}) => {
  const { updateToast } = useToastStore();
  const [resources, setResources] = useState<AvailableResources>({});
  const { vehicles, users, equipments, polygons, points, lines } = resources;

  const employeeSelectItems = useMemo(() => {
    if (!users) return [];
    return users.map(({ _id, fullName, profile }) => {
      let label = '';
      const { badgeNumber, title } = profile?.employmentInformation || {};
      if (badgeNumber) {
        label += badgeNumber;
      }
      if (fullName) {
        label += label ? ` - ${fullName}` : fullName;
      }
      if (title) {
        label += label ? ` - ${title}` : title;
      }
      return {
        label,
        value: _id,
        disabled:
          isForReassignment &&
          passedResources?.users?.find((user) => user._id === _id),
      };
    });
  }, [users]);

  const getSelectItems = useCallback(
    (
      itemList:
        | IEquipment[]
        | IPolygonLayer[]
        | ILineLayer[]
        | IPointLayer[]
        | IVehicle[]
        | undefined,
      itemType: ResourceItemType,
    ) => {
      if (!itemList) {
        return [];
      }
      return itemList.map(({ _id, name }) => ({
        label: name,
        value: _id,
        disabled:
          isForReassignment &&
          passedResources?.[itemType]?.find((item) => item._id === _id),
      }));
    },
    [isForReassignment, passedResources],
  );

  // Memoize each option list so its reference is stable across re-renders.
  // An unstable list reference makes the autocomplete value reference change,
  // which resets the user's in-progress typed search text.
  const vehicleSelectItems = useMemo(
    () => getSelectItems(vehicles, ResourceItemType.VEHICLES),
    [vehicles, getSelectItems],
  );
  const equipmentSelectItems = useMemo(
    () => getSelectItems(equipments, ResourceItemType.EQUIPMENTS),
    [equipments, getSelectItems],
  );
  const polygonSelectItems = useMemo(
    () => getSelectItems(polygons, ResourceItemType.POLYGONS),
    [polygons, getSelectItems],
  );
  const pointSelectItems = useMemo(
    () => getSelectItems(points, ResourceItemType.POINTS),
    [points, getSelectItems],
  );
  const lineSelectItems = useMemo(
    () => getSelectItems(lines, ResourceItemType.LINES),
    [lines, getSelectItems],
  );

  const fetchResources = async () => {
    // Pin the target agency for the resources fetch, then restore the
    // previous value — wiping it breaks every subsequent request with
    // "X-HELPTAPP-AGENCY must be specified".
    const previousAgencyId = localStorage.getItem(
      LocalStorageItems.CurrentAgencyId,
    );
    const isPinned = Boolean(agencyId && agencyId !== previousAgencyId);
    try {
      if (agencyId) {
        localStorage.setItem(LocalStorageItems.CurrentAgencyId, agencyId);
      }
      const res = await UnitApi.getAvailableResources(
        currentUnitType?._id,
        unitId,
        isForReassignment,
      );

      // Merge fetched resources with passed resources
      const mergedResources = { ...res.data };
      const resourceTypes: (keyof AvailableResources)[] = [
        'vehicles',
        'users',
        'equipments',
        'polygons',
        'points',
        'lines',
      ];

      resourceTypes.forEach((type) => {
        if (passedResources && passedResources[type]) {
          const existingItems = mergedResources[type] || [];
          const additionalItems = passedResources[type] || [];

          // Merge and remove duplicates
          const uniqueItems = [...existingItems, ...additionalItems].filter(
            (item, index, self) =>
              index === self.findIndex((t) => t._id === item._id),
          );

          // Type-safe assignment
          (mergedResources[type] as any) = uniqueItems;
        }
      });

      if (setOutsideResources) {
        setOutsideResources(mergedResources);
      }
      setResources(mergedResources);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      if (isPinned) {
        if (previousAgencyId) {
          localStorage.setItem(
            LocalStorageItems.CurrentAgencyId,
            previousAgencyId,
          );
        } else {
          localStorage.removeItem(LocalStorageItems.CurrentAgencyId);
        }
      }
    }
  };

  useEffect(() => {
    if (currentUnitType) {
      fetchResources();
    }
  }, [currentUnitType?._id]);

  const getOptions = (resourceType: ResourceType) => {
    switch (resourceType) {
      case ResourceType.VEHICLE:
        return vehicleSelectItems;
      case ResourceType.PEOPLE:
        return employeeSelectItems;
      case ResourceType.EQUIPMENT:
        return equipmentSelectItems;
      case ResourceType.POLYGON_LAYER:
        return polygonSelectItems;
      case ResourceType.POINT_LAYER:
        return pointSelectItems;
      case ResourceType.LINE_LAYER:
        return lineSelectItems;
      default:
        return [];
    }
  };

  const validateResourceSelectedQuantity = (
    selectedLength: number,
    min: number,
    max?: number,
    errorLabel?: string,
  ) => {
    if (selectedLength < min) {
      if (max) {
        return `You must assign between ${min} and ${max} ${errorLabel}s to this unit`;
      }
      return min > 1
        ? `You must assign at least ${min} ${errorLabel}s to this unit`
        : `You must assign at least ${min} ${errorLabel} to this unit`;
    }

    if (max && selectedLength > max) {
      return `You must assign between ${min} and ${max} ${errorLabel}s to this unit`;
    }

    return true;
  };

  const equipmentWithSubTypeValidator = (
    value: string[],
    errorLabel: string,
  ) => {
    const filteredValue = value?.filter((item) => {
      const options = getOptions(ResourceType.EQUIPMENT);
      return options.find((option) => option.value === item);
    });

    // We are certain equipment resource items exist if we got to this method, hence the type assertion.
    const allEquipments = (currentUnitType?.resources as IResource[]).filter(
      (eq) => eq.type === ResourceType.EQUIPMENT,
    );

    // Validate each equipment resource item
    for (const equipment of allEquipments) {
      // Out of the selected value (a list of equipment ids) for this field. We need to
      // find the correspending equipment resource, and filter the selected values
      // that are matching the current resource item being validated (by subtype).
      const selectedSubTypeEquipment = filteredValue
        ?.map((id) => equipments?.find((eq) => eq._id === id))
        .filter(
          (eq): eq is IEquipment =>
            eq !== undefined && eq.type === equipment.subType,
        );

      const validationResult = validateResourceSelectedQuantity(
        selectedSubTypeEquipment?.length,
        equipment.min,
        equipment.max,
        equipment.subType?.toLowerCase(),
      );

      if (
        !selectedSubTypeEquipment ||
        equipment.min > selectedSubTypeEquipment.length
      ) {
        return `You must assign at least ${equipment.min} ${errorLabel}s to this unit`;
      }

      if (equipment.max && equipment.max < value.length) {
        return `You must assign between ${equipment.min} and ${equipment.max} ${errorLabel}s to this unit`;
      }

      if (withUnitOnly) return true;

      // Exit early if the resource item conditions are not met.
      if (validationResult !== true) {
        return validationResult;
      }
    }

    // If we got here it means that all equipment resource items were validated successfully.
    return true;
  };

  const validator = (
    value: string[],
    resource: IResource,
    errorLabel: string,
  ) => {
    const { min, max, type } = resource;
    if (!value || min > +value?.length) {
      return `You must assign at least ${min} ${errorLabel}s to this unit`;
    }

    if (max && max < value.length) {
      return `You must assign between ${min} and ${max} ${errorLabel}s to this unit`;
    }

    if (withUnitOnly) return true;

    const filteredValue = value?.filter((item) => {
      const options = getOptions(type);
      return options.find((option) => option.value === item);
    });

    return validateResourceSelectedQuantity(
      filteredValue.length,
      min,
      max,
      errorLabel,
    );
  };

  const filteredTypeResources = useMemo(() => {
    if (!currentUnitType?.resources) return [];

    const uniqueResources = currentUnitType.resources.reduce<IResource[]>(
      (acc, resource) => {
        if (!acc.some((item) => item.type === resource.type)) {
          acc.push(resource);
        }
        return acc;
      },
      [],
    );

    return withUnitOnly
      ? uniqueResources.filter((item) => item.isDefault)
      : uniqueResources;
  }, [currentUnitType, withUnitOnly]);

  return (
    <>
      {filteredTypeResources.map((resourceField) => {
        const formField = UnitResourceFormFields[resourceField.type];
        if (!formField) return null;
        return (
          <Grid key={formField.name} xs={12} {...formField.grid} item>
            <RenderFormField
              {...formField}
              items={getOptions(resourceField.type)}
              rules={{
                validate: skipValidation
                  ? () => true
                  : (value: string[]) =>
                      resourceField.type === ResourceType.EQUIPMENT &&
                      !!resourceField.subType
                        ? equipmentWithSubTypeValidator(
                            value,
                            formField.errorLabel,
                          )
                        : validator(value, resourceField, formField.errorLabel),
              }}
            />
          </Grid>
        );
      })}
    </>
  );
};
